import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('flexx_access_token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const SESSION_COLORS = {
  low: '#888',
  medium: '#C9A227',
  high: '#22C55E',
  highest: '#C9A227',
};

/** Tiny inline SVG sparkline. Renders a smoothed polyline + filled area
 *  from a series of `{t, c}` bars. Color picks itself from first→last
 *  delta so the line matches the green/red pct chip in the popover. */
const Sparkline = ({ bars, width = 80, height = 22 }) => {
  if (!Array.isArray(bars) || bars.length < 2) return null;
  const closes = bars.map((b) => b.c).filter((n) => typeof n === 'number');
  if (closes.length < 2) return null;
  const min = Math.min(...closes);
  const max = Math.max(...closes);
  const range = max - min || 1;
  const stepX = width / (closes.length - 1);
  const points = closes.map((c, i) => {
    const x = i * stepX;
    const y = height - ((c - min) / range) * height;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });
  const polyline = points.join(' ');
  const areaPath = `M0,${height} L${points.join(' L')} L${width},${height} Z`;
  const isUp = closes[closes.length - 1] >= closes[0];
  const stroke = isUp ? '#22c55e' : '#ef4444';
  const fill = isUp ? 'rgba(34,197,94,0.16)' : 'rgba(239,68,68,0.16)';
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ verticalAlign: 'middle', flexShrink: 0 }}
      data-testid="axel-market-badge-spark"
      aria-hidden
    >
      <path d={areaPath} fill={fill} />
      <polyline
        points={polyline}
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const AxelMarketBadge = () => {
  const [market, setMarket] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  // Sparklines fetched lazily — only when popover opens. Cached in
  // component state until reload to avoid hammering the upstream cache.
  const [sparks, setSparks] = useState({});  // { "XAU/USD": [{t,c}, ...] }

  useEffect(() => {
    let cancelled = false;
    const fetchMarket = async () => {
      try {
        const res = await axios.get(`${API}/market/context`, {
          headers: authHeaders(),
          withCredentials: true,
        });
        if (!cancelled) setMarket(res.data);
      } catch (err) {
        // Silent fail — badge simply doesn't render until data is available
        if (!cancelled) console.error('[AxelMarketBadge]', err?.message || err);
      }
    };
    fetchMarket();
    const interval = setInterval(fetchMarket, 5 * 60 * 1000); // 5 min
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Lazy-fetch the sparkline series for each pair the moment the popover
  // opens. The backend caches 5 min so re-opens within the window cost zero
  // upstream calls.
  useEffect(() => {
    if (!popoverOpen) return;
    const symbols = ['XAU/USD', 'BTC/USD', 'EUR/USD'];
    let cancelled = false;
    (async () => {
      for (const sym of symbols) {
        if (sparks[sym]) continue;
        try {
          const res = await axios.get(`${API}/market-intel/sparkline`, {
            params: { symbol: sym },
            headers: authHeaders(),
            withCredentials: true,
          });
          if (cancelled) return;
          const bars = Array.isArray(res.data?.bars) ? res.data.bars : null;
          if (bars && bars.length) {
            setSparks((s) => ({ ...s, [sym]: bars }));
          }
        } catch {
          /* silent — sparkline is a nice-to-have, never block the popover */
        }
      }
    })();
    return () => { cancelled = true; };
  }, [popoverOpen, sparks]);

  // Auto-collapse the popover after 8s — long enough to read the
  // sparklines, short enough to feel snappy on mobile.
  useEffect(() => {
    if (!popoverOpen) return;
    const t = setTimeout(() => setPopoverOpen(false), 8000);
    return () => clearTimeout(t);
  }, [popoverOpen]);

  if (!market) return null;

  const session = market.session || {};
  const hasUpcoming = Array.isArray(market.calendar) && market.calendar.some((e) => e.status === 'upcoming' && e.impact === 'high');
  const nextRedEvent = (market.calendar || []).find((e) => e.status === 'upcoming' && e.impact === 'high');
  const gold = market.prices?.XAUUSD?.price;
  const goldPct = market.prices?.XAUUSD?.change_percent;
  const btc = market.prices?.BTCUSD?.price;
  const btcPct = market.prices?.BTCUSD?.change_percent;
  const eur = market.prices?.EURUSD?.price;
  const eurPct = market.prices?.EURUSD?.change_percent;

  const fmtPct = (p) => {
    if (typeof p !== 'number') return null;
    const sign = p >= 0 ? '+' : '';
    return `${sign}${p.toFixed(2)}%`;
  };
  const pctColor = (p) => (typeof p !== 'number' ? '#888' : p >= 0 ? '#22c55e' : '#ef4444');

  return (
    <>
      <style>{`
        @keyframes axelBadgePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.55; transform: scale(1.25); }
        }
        .axel-market-badge {
          display: flex; align-items: center; gap: 10px;
          padding: 6px 14px; background: #111;
          border-radius: 20px; border: 1px solid #222;
          font-size: 12px; cursor: default;
          transition: border-color 0.2s ease, background 0.2s ease;
          flex-shrink: 0;            /* never let parent flex squeeze us out */
          margin-left: auto;          /* push to the right when in a flex row */
        }
        .axel-market-badge:hover { border-color: #C9A22755; background: #151515; }
        .axel-market-badge-sep {
          color: #333; font-size: 11px; user-select: none;
        }
        .axel-market-badge-price {
          color: #d4d4d4; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 11px; font-weight: 600; letter-spacing: 0.2px; white-space: nowrap;
        }
        /* EUR is the lowest-priority instrument — drop it below 1100px */
        @media (max-width: 1100px) {
          .axel-market-badge-eur,
          .axel-market-badge-eur-sep { display: none !important; }
        }
        /* Mobile compact mode: keep ONLY the pulsing dot + GOLD price + ⚠️.
           Hide session text, BTC, EUR, and their leading separators so
           the badge always fits in the mobile header next to the logo. */
        @media (max-width: 768px) {
          .axel-market-badge {
            padding: 4px 10px;
            font-size: 11px;
            gap: 6px;
          }
          .axel-market-badge-session,
          .axel-market-badge-sep-session,
          .axel-market-badge-btc,
          .axel-market-badge-sep-btc,
          .axel-market-badge-eur,
          .axel-market-badge-eur-sep { display: none !important; }
        }

        /* Tap-popover: full intel quick-view, anchored under the pill.
           Auto-collapses after 4s (handled in JS). Safe at all viewports. */
        @keyframes axelPopoverIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .axel-market-badge-popover {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          min-width: 300px;
          background: #0d0d0d;
          border: 1px solid #2a2a2a;
          border-radius: 12px;
          padding: 10px 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,162,39,0.08);
          font-size: 12px;
          color: #d4d4d4;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          z-index: 1000;
          cursor: default;
          animation: axelPopoverIn 0.18s ease-out;
        }
        .axel-market-badge-popover-row {
          display: flex; justify-content: space-between; align-items: center;
          gap: 16px; padding: 4px 0; white-space: nowrap;
        }
        .axel-market-badge-popover-label {
          color: #888; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;
        }
      `}</style>
      <div
        className="axel-market-badge"
        data-testid="axel-market-badge"
        title={hasUpcoming ? 'High-impact event upcoming — trade with care' : `${session.name || 'Market'} session`}
        onClick={() => setPopoverOpen((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPopoverOpen((v) => !v); } }}
        style={{ cursor: 'pointer', position: 'relative' }}
      >
        <div
          data-testid="axel-market-badge-dot"
          style={{
            width: 8, height: 8, borderRadius: '50%',
            background: '#22C55E',
            boxShadow: '0 0 8px #22C55E',
            animation: 'axelBadgePulse 2s infinite',
            flexShrink: 0,
          }}
        />
        <span
          data-testid="axel-market-badge-session"
          className="axel-market-badge-session"
          style={{
            fontWeight: 700,
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: SESSION_COLORS[session.quality] || '#888',
          }}
        >
          {session.name || 'Market Closed'}
        </span>
        {typeof gold === 'number' && (
          <>
            <span className="axel-market-badge-sep axel-market-badge-sep-session" aria-hidden>|</span>
            <span data-testid="axel-market-badge-gold" className="axel-market-badge-price axel-market-badge-gold">
              GOLD {gold.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              {fmtPct(goldPct) && (
                <span data-testid="axel-market-badge-gold-pct" style={{ color: pctColor(goldPct), marginLeft: 6, fontWeight: 700 }}>
                  {fmtPct(goldPct)}
                </span>
              )}
            </span>
          </>
        )}
        {typeof btc === 'number' && (
          <>
            <span className="axel-market-badge-sep axel-market-badge-sep-btc" aria-hidden>|</span>
            <span data-testid="axel-market-badge-btc" className="axel-market-badge-price axel-market-badge-btc">
              BTC {Math.round(btc).toLocaleString('en-US')}
              {fmtPct(btcPct) && (
                <span data-testid="axel-market-badge-btc-pct" style={{ color: pctColor(btcPct), marginLeft: 6, fontWeight: 700 }}>
                  {fmtPct(btcPct)}
                </span>
              )}
            </span>
          </>
        )}
        {typeof eur === 'number' && (
          <>
            <span className="axel-market-badge-sep axel-market-badge-eur-sep" aria-hidden>|</span>
            <span data-testid="axel-market-badge-eur" className="axel-market-badge-price axel-market-badge-eur">
              EUR {eur.toFixed(4)}
              {fmtPct(eurPct) && (
                <span data-testid="axel-market-badge-eur-pct" style={{ color: pctColor(eurPct), marginLeft: 6, fontWeight: 700 }}>
                  {fmtPct(eurPct)}
                </span>
              )}
            </span>
          </>
        )}
        {hasUpcoming && (
          <>
            <span className="axel-market-badge-sep" aria-hidden>|</span>
            <div
              data-testid="axel-market-badge-alert"
              style={{ color: '#FF4444', fontSize: 14, lineHeight: 1 }}
              aria-label="High-impact event upcoming"
            >
              ⚠️
            </div>
          </>
        )}
        {popoverOpen && (
          <div
            data-testid="axel-market-badge-popover"
            className="axel-market-badge-popover"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Market intel quick view"
          >
            <div className="axel-market-badge-popover-row">
              <span className="axel-market-badge-popover-label">Session</span>
              <span style={{ color: SESSION_COLORS[session.quality] || '#888', fontWeight: 700 }}>
                {session.name || '—'} <span style={{ opacity: 0.55 }}>({session.quality || 'unknown'})</span>
              </span>
            </div>
            {typeof gold === 'number' && (
              <div className="axel-market-badge-popover-row">
                <span className="axel-market-badge-popover-label">Gold</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Sparkline bars={sparks['XAU/USD']} />
                  <span>
                    {gold.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    {fmtPct(goldPct) && (
                      <span style={{ color: pctColor(goldPct), marginLeft: 8, fontWeight: 700 }}>
                        {fmtPct(goldPct)}
                      </span>
                    )}
                  </span>
                </span>
              </div>
            )}
            {typeof btc === 'number' && (
              <div className="axel-market-badge-popover-row">
                <span className="axel-market-badge-popover-label">Bitcoin</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Sparkline bars={sparks['BTC/USD']} />
                  <span>
                    {Math.round(btc).toLocaleString('en-US')}
                    {fmtPct(btcPct) && (
                      <span style={{ color: pctColor(btcPct), marginLeft: 8, fontWeight: 700 }}>
                        {fmtPct(btcPct)}
                      </span>
                    )}
                  </span>
                </span>
              </div>
            )}
            {typeof eur === 'number' && (
              <div className="axel-market-badge-popover-row">
                <span className="axel-market-badge-popover-label">EUR/USD</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Sparkline bars={sparks['EUR/USD']} />
                  <span>
                    {eur.toFixed(4)}
                    {fmtPct(eurPct) && (
                      <span style={{ color: pctColor(eurPct), marginLeft: 8, fontWeight: 700 }}>
                        {fmtPct(eurPct)}
                      </span>
                    )}
                  </span>
                </span>
              </div>
            )}
            <div className="axel-market-badge-popover-row" style={{ borderTop: '1px dashed #2a2a2a', paddingTop: 6, marginTop: 4 }}>
              <span className="axel-market-badge-popover-label">Next red event</span>
              <span style={{ color: nextRedEvent ? '#FF4444' : '#888' }}>
                {nextRedEvent
                  ? `${nextRedEvent.time} ${nextRedEvent.currency} · ${nextRedEvent.event}`
                  : 'None today'}
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AxelMarketBadge;
